import "@fontsource-variable/manrope";
import { experienceConfig } from "./config/experience.js";
import {
  animalHotspots,
  validateAnimalHotspots
} from "./config/animals.js";
import { ExperienceStore } from "./core/store.js";
import {
  detectPerformanceTier,
  PerformanceMonitor,
  prefersReducedMotion
} from "./core/performance.js";
import { VideoController } from "./media/video-controller.js";
import { AudioController } from "./media/audio-controller.js";
import { ForestScene } from "./experience/forest-scene.js";
import { ScrollController } from "./interaction/scroll-controller.js";
import { CursorController } from "./interaction/cursor-controller.js";
import { AnimalController } from "./interaction/animal-controller.js";
import { ProjectDialogController } from "./interaction/project-dialog.js";
import { AmbientInteractions } from "./interaction/ambient-interactions.js";
import { PortfolioController } from "./interaction/portfolio-controller.js";

function requireElement(selector) {
  const element = document.querySelector(selector);
  if (!element) {
    throw new Error(`Required element not found: ${selector}`);
  }
  return element;
}

function nextFrame() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });
}

function waitForImageDecode(image, timeoutMs = 1400) {
  if (!(image instanceof HTMLImageElement)) {
    return Promise.resolve(false);
  }
  if (image.complete && image.naturalWidth > 0) {
    return Promise.resolve(true);
  }

  const decodePromise =
    typeof image.decode === "function"
      ? image.decode().then(() => true)
      : new Promise((resolve) => {
          const done = () => resolve(image.naturalWidth > 0);
          image.addEventListener("load", done, { once: true });
          image.addEventListener("error", () => resolve(false), {
            once: true
          });
        });

  return Promise.race([
    decodePromise.catch(() => false),
    new Promise((resolve) =>
      window.setTimeout(() => resolve(false), timeoutMs)
    )
  ]);
}

function createLoadingController({ screen, store, reduced }) {
  const minimumDuration = reduced ? 0 : 3200;
  const startedAt = performance.now();
  let dismissed = false;
  let intent = false;
  let pointerFrame = 0;
  let lastPointer = { x: 0, y: 0 };
  const cleanup = [];

  const setIntent = () => {
    intent = true;
    screen.dataset.loadingState = "intent";
    document.body.classList.add("is-loading-intent");
  };

  const pointerHandler = (event) => {
    lastPointer = {
      x: (event.clientX / window.innerWidth - 0.5) * 32,
      y: (event.clientY / window.innerHeight - 0.5) * 24
    };
    if (pointerFrame) {
      return;
    }
    pointerFrame = window.requestAnimationFrame(() => {
      pointerFrame = 0;
      screen.style.setProperty(
        "--loader-pointer-x",
        `${lastPointer.x.toFixed(2)}px`
      );
      screen.style.setProperty(
        "--loader-pointer-y",
        `${lastPointer.y.toFixed(2)}px`
      );
    });
  };

  const wheelHandler = (event) => {
    if (Math.abs(event.deltaY) > 8) {
      setIntent();
    }
  };
  const touchHandler = () => setIntent();
  const keyHandler = (event) => {
    if (
      ["ArrowDown", "PageDown", " ", "Spacebar"].includes(event.key)
    ) {
      setIntent();
    }
  };

  if (!reduced) {
    window.addEventListener("pointermove", pointerHandler, {
      passive: true
    });
    window.addEventListener("wheel", wheelHandler, { passive: true });
    window.addEventListener("touchmove", touchHandler, { passive: true });
    window.addEventListener("keydown", keyHandler);
    cleanup.push(
      () => window.removeEventListener("pointermove", pointerHandler),
      () => window.removeEventListener("wheel", wheelHandler),
      () => window.removeEventListener("touchmove", touchHandler),
      () => window.removeEventListener("keydown", keyHandler)
    );
  }

  const waitForContent = async () => {
    const hero = document.querySelector("#hero");
    const firstProjectImage = document.querySelector(
      ".project-overview img"
    );
    await Promise.all([
      document.fonts?.ready?.catch(() => {}) ?? Promise.resolve(),
      waitForImageDecode(firstProjectImage),
      nextFrame()
    ]);
    return Boolean(hero);
  };

  return {
    getSnapshot() {
      return {
        dismissed,
        intent,
        elapsed: Math.round(performance.now() - startedAt),
        state: screen.dataset.loadingState ?? "opening"
      };
    },
    async reveal(mediaReady) {
      await Promise.all([
        mediaReady.catch(() => false),
        waitForContent()
      ]);
      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(
        0,
        (intent ? 900 : minimumDuration) - elapsed
      );
      if (remaining > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remaining));
      }
      await nextFrame();
      screen.dataset.loadingState = "unveiling";
      document.body.classList.add("is-unveiling");
      await new Promise((resolve) => window.setTimeout(resolve, 180));
      document.body.classList.add("is-ready");
      dismissed = true;
      screen.dataset.loadingState = "complete";
      store.emit("loading:complete", this.getSnapshot());
    },
    destroy() {
      cleanup.forEach((dispose) => dispose());
      if (pointerFrame) {
        window.cancelAnimationFrame(pointerFrame);
      }
    }
  };
}

function setupCopyEmail(store) {
  const button = document.querySelector("[data-copy-email]");
  const liveRegion = requireElement("#live-region");
  if (!button) {
    return () => {};
  }

  const handler = async () => {
    const email = button.dataset.copyEmail;
    try {
      await navigator.clipboard.writeText(email);
      button.textContent = "已复制";
      liveRegion.textContent = `邮箱 ${email} 已复制。`;
      window.setTimeout(() => {
        button.textContent = "复制邮箱";
      }, 1600);
    } catch (error) {
      liveRegion.textContent = `复制失败，请手动复制 ${email}。`;
      store.emit("clipboard:error", { error });
    }
  };
  button.addEventListener("click", handler);
  return () => button.removeEventListener("click", handler);
}

function setupInputModality(store) {
  let current = "unknown";
  const setInput = (input) => {
    if (current === input) {
      return;
    }
    current = input;
    document.body.dataset.input = input;
    store.patch(
      { interaction: { input } },
      "interaction:input"
    );
  };
  const pointerHandler = (event) => {
    setInput(event.pointerType === "touch" ? "touch" : "pointer");
  };
  const keyHandler = (event) => {
    if (event.key === "Tab" || event.key.startsWith("Arrow")) {
      setInput("keyboard");
    }
  };
  window.addEventListener("pointerdown", pointerHandler, { passive: true });
  window.addEventListener("keydown", keyHandler);
  return () => {
    window.removeEventListener("pointerdown", pointerHandler);
    window.removeEventListener("keydown", keyHandler);
    delete document.body.dataset.input;
  };
}

function createPerformanceHud({ store, scene, video, animals }) {
  if (
    !import.meta.env.DEV ||
    !new URLSearchParams(window.location.search).has("perf")
  ) {
    return { update() {}, destroy() {} };
  }
  const hud = document.createElement("pre");
  hud.className = "performance-hud";
  document.body.append(hud);
  let lastUpdate = 0;
  return {
    update(timestamp) {
      if (timestamp - lastUpdate < 250) {
        return;
      }
      lastUpdate = timestamp;
      const state = store.get();
      const sceneStats = scene.getStats();
      hud.textContent = [
        `FPS       ${state.performance.fps}`,
        `TIER      ${state.performance.tier}`,
        `SOURCE    ${video.currentSource?.id ?? "poster"}`,
        `TIME      ${state.media.currentTime.toFixed(2)}s`,
        `SECTION   ${state.section}`,
        `PARTICLES ${sceneStats.particles}`,
        `DRAWS     ${sceneStats.drawCalls}`,
        `ANIMAL    ${animals.getDebugSnapshot()?.id ?? "none"}`,
        `AUDIO     ${state.audio.enabled ? state.audio.gain.toFixed(2) : "off"}`
      ].join("\n");
    },
    destroy() {
      hud.remove();
    }
  };
}

async function bootstrap() {
  validateAnimalHotspots();
  const reduced = prefersReducedMotion();
  const tier = detectPerformanceTier();
  const store = new ExperienceStore({
    performance: {
      tier,
      fps: 60,
      webglAvailable: true
    }
  });

  const videoElement = requireElement("#forest-video");
  videoElement.poster = experienceConfig.media.poster;
  const loading = createLoadingController({
    screen: requireElement("#loading-screen"),
    store,
    reduced
  });
  const gate = requireElement("#experience-gate");
  const mist = requireElement(".stage__mist");
  const scene = new ForestScene({
    canvas: requireElement("#forest-canvas"),
    store,
    config: experienceConfig.scene,
    tier
  });
  scene.init();

  const scroll = new ScrollController({
    store,
    tier,
    reduced,
    backToTop: requireElement("#back-to-top")
  });
  scroll.init();

  const cursor = new CursorController({
    element: requireElement("#cursor"),
    dialogElement: requireElement("[data-dialog-cursor]"),
    store,
    reduced
  });
  cursor.init();

  const video = new VideoController({
    video: videoElement,
    store,
    config: experienceConfig.media,
    gate,
    mist
  });
  const audio = new AudioController({
    video: videoElement,
    store,
    config: experienceConfig.audio,
    button: requireElement("#audio-toggle")
  });
  const animals = new AnimalController({
    hotspots: animalHotspots,
    store,
    video,
    scene,
    audio,
    cursor,
    aura: requireElement("#animal-aura"),
    cue: document.querySelector("[data-animal-cue]"),
    config: experienceConfig.animals,
    tier
  });
  animals.init();

  const liveRegion = requireElement("#live-region");
  const projectDialog = new ProjectDialogController({
    dialog: requireElement("#project-dialog"),
    store,
    scroll
  });
  projectDialog.init();

  const portfolio = new PortfolioController({
    store,
    scroll,
    liveRegion
  });
  portfolio.init();

  const ambient = new AmbientInteractions({
    config: experienceConfig.features,
    scene,
    store,
    mistButton: requireElement("#mist-toggle"),
    windButton: requireElement("#wind-control")
  });
  ambient.init(tier);

  const removeCopyEmail = setupCopyEmail(store);
  const removeInputModality = setupInputModality(store);
  const monitor = new PerformanceMonitor(
    store,
    experienceConfig.performance
  );
  const hud = createPerformanceHud({ store, scene, video, animals });
  const removeMediaError = store.on("media:error", () => {
    liveRegion.textContent =
      "动态视频未能加载，页面已切换到静态森林模式。";
  });

  const videoInitialization = video.init(tier);
  const loadingFallback = window.setTimeout(() => {
    document.body.classList.add("is-unveiling", "is-ready");
  }, 12000);
  loading.reveal(videoInitialization).finally(() => {
    window.clearTimeout(loadingFallback);
  });

  let frame = 0;
  let lastTimestamp = performance.now();
  let running = true;
  const update = (timestamp) => {
    if (!running) {
      return;
    }
    const deltaSeconds = Math.min(
      0.05,
      Math.max(0.001, (timestamp - lastTimestamp) / 1000)
    );
    lastTimestamp = timestamp;

    if (!document.hidden) {
      video.update();
      const scrollVelocity = scroll.update();
      audio.setScrollVelocity(scrollVelocity);
      const pointer = cursor.update(deltaSeconds);
      scene.setPointer(
        (pointer.x / window.innerWidth - 0.5) * 2,
        -((pointer.y / window.innerHeight - 0.5) * 2)
      );
      animals.update(timestamp);
      scene.update(deltaSeconds, scrollVelocity);
      audio.update(deltaSeconds);
      monitor.sample(timestamp);
      hud.update(timestamp);
    }
    frame = window.requestAnimationFrame(update);
  };
  frame = window.requestAnimationFrame(update);

  window.__FOREST_DEBUG__ = {
    snapshot: () => structuredClone(store.get()),
    setMediaTime: (time) => video.setCurrentTime(time),
    getHotspot: () => animals.getDebugSnapshot(),
    openProject: (id) => projectDialog.open(id),
    closeProject: () => projectDialog.close(),
    setAudio: (enabled) => audio.toggle(enabled),
    triggerWind: () => ambient.triggerWind(),
    triggerAnimalCue: () => animals.triggerObservationCue(),
    setProjectFilter: (id) => portfolio.setFilter(id),
    getProjectFilter: () => portfolio.getFilter(),
    setActiveSkill: (id) => portfolio.setActiveSkill(id),
    getActiveSkill: () => portfolio.getActiveSkill(),
    getInteractionSnapshot: () => ({
      ...structuredClone(store.get().interaction),
      projectFilter: portfolio.getFilter(),
      activeSkillId: portfolio.getActiveSkill(),
      activeProjectId:
        projectDialog.getActiveProject() ?? portfolio.getActiveProject()
    }),
    getActiveProject: () =>
      projectDialog.getActiveProject() ?? portfolio.getActiveProject(),
    getLoadingSnapshot: () => loading.getSnapshot()
  };

  const destroy = () => {
    running = false;
    window.cancelAnimationFrame(frame);
    window.clearTimeout(loadingFallback);
    loading.destroy();
    removeMediaError();
    removeCopyEmail();
    removeInputModality();
    hud.destroy();
    ambient.destroy();
    portfolio.destroy();
    projectDialog.destroy();
    animals.destroy();
    audio.destroy();
    video.destroy();
    cursor.destroy();
    scroll.destroy();
    scene.destroy();
    delete window.__FOREST_DEBUG__;
  };
  window.addEventListener("pagehide", destroy, { once: true });
}

bootstrap().catch((error) => {
  console.error("Forest experience failed to initialize.", error);
  document.body.classList.add("is-static", "is-ready");
  const liveRegion = document.querySelector("#live-region");
  if (liveRegion) {
    liveRegion.textContent =
      "动态体验初始化失败，页面已切换到静态阅读模式。";
  }
});
